import { Component, computed, input, output, signal, Signal, WritableSignal, inject } from '@angular/core';
import {
  BoFormsModule,
  FieldPathDirective,
  FormComponent,
  FormNonFieldErrorsComponent,
  FormOptionsDirective,
} from '@bo/ng-forms';
import { CardScheduleStatus, CardTopup, CardTopupRequest } from '../../../../../_interfaces/card-topups.interface';
import { OffcanvasService } from '../../../../../_services/offcanvas-service/offcanvas.service';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ToastService } from '../../../../../_services/toast-service/toast.service';
import { CardsApiService } from '../../../../../_services/cards-service/cards-api/cards-api.service';
import { Method } from '@bo/ng-api';
import { CardTopupsApiService } from '../../../../../_services/topup-service/api/card-topups-api.service';
import { TopUpFrequency } from '../../../../../_interfaces/employee.interface';
import { map, Observable, of } from 'rxjs';
import { NgbDateParserFormatter, NgbDateStruct, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmationRemoveModalComponent } from '../../../../../shared/components/modals/confirmation-remove-modal/confirmation-remove-modal.component';
import { CurrencyPipe, NgClass } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { LoaderComponent } from '../../../../../shared/components/loader/loader.component';
import { FieldComponent } from '../../../../../shared/components/forms/form-field/form-field.component';
import { maskConfig } from '../../../../../shared/configs/configs';
import { Card, CardLimits, CardType } from '../../../../../_interfaces/cards.interface';
import { DateRangePickerComponent } from '../../../../../shared/components/forms/date-range-picker/date-range-picker.component';
import { formatDateDash, formatDateSlash } from '../../../../../shared/utils/general-utils';
import { SubmitComponent } from '../../../../../shared/components/forms/submit/submit.component';
import { topUpSelectOptions } from '../../utils/utils';
import { CardTopupsService } from '../../../../../_services/topup-service/card-topups.service';
import { statusBadgeDepartments } from '../../../../../shared/components/status-badge/status-badge.component';
import { SelectTopupFrequencyComponent } from '../../../../../shared/components/forms/select-topup-frequency/select-topup-frequency.component';
import { CardLimitDisplayComponent } from '../../../../../shared/components/cards/card-limit-display/card-limit-display.component';
import { FetchError } from '../../../../../shared/interfaces/project-interfaces';
import { HttpErrorResponse } from '@angular/common/http';
import { EmptyIndicatorPipe } from '../../../../../shared/pipes/empty-indicator.pipe';

@Component({
  selector: 'app-card-topup-form',
  imports: [
    ReactiveFormsModule,
    LoaderComponent,
    FormNonFieldErrorsComponent,
    FieldComponent,
    FormOptionsDirective,
    FieldPathDirective,
    NgClass,
    CurrencyPipe,
    DateRangePickerComponent,
    SubmitComponent,
    BoFormsModule,
    SelectTopupFrequencyComponent,
    CardLimitDisplayComponent,
    EmptyIndicatorPipe,
  ],
  templateUrl: './card-topup-form.component.html',
})
export class CardTopupFormComponent extends FormComponent<CardTopupRequest> {
  readonly offcanvasService = inject(OffcanvasService);
  readonly route = inject(ActivatedRoute);
  readonly router = inject(Router);
  readonly modalService = inject(NgbModal);
  readonly toastService = inject(ToastService);
  readonly cardsApiService = inject(CardsApiService);
  readonly cardTopupsApiService = inject(CardTopupsApiService);
  readonly cardTopupsService = inject(CardTopupsService);
  readonly ngbDateParserFormatter = inject(NgbDateParserFormatter);

  protected readonly fields: string[] = ['amount', 'date_start', 'date_end'];
  protected readonly selectOptions = topUpSelectOptions;
  protected readonly TopUpFrequency = TopUpFrequency;
  protected readonly CardType = CardType;
  protected readonly statusBadgeDepartments = statusBadgeDepartments;
  protected readonly Boolean = Boolean;
  protected readonly maskConfig = maskConfig;
  protected fetchError = signal<FetchError | null>(null);

  protected rule: CardTopup | undefined;
  protected isRecurring: Signal<boolean> = computed<boolean>(() => this.router.url.includes('recurring'));
  topupRuleId = signal<string>('');
  protected hasCardLimitError = signal<boolean>(false);
  protected cardLimits: Signal<CardLimits | null> = computed(() => this.cardTopupsService.cardLimits());
  protected dateFrom: WritableSignal<NgbDateStruct | null> = signal(null);
  protected dateTo: WritableSignal<NgbDateStruct | null> = signal(null);
  protected staticDateDisplay = signal('');
  protected returnUrl = computed(() => this.cardTopupsService.conditionalReturnUrl());

  protected isStatusActive = computed<boolean>(
    () => this.selectedRule()?.schedule_status === CardScheduleStatus.Active,
  );
  protected isStatusPending = computed<boolean>(
    () => this.selectedRule()?.schedule_status === CardScheduleStatus.Pending,
  );
  protected isStatusCompleted = computed<boolean>(
    () => this.selectedRule()?.schedule_status === CardScheduleStatus.Completed,
  );

  protected isDisabledScheduledRule = computed<boolean>(
    () => Boolean(this.topupRuleId()) && !this.isRecurring() && (this.isStatusCompleted() || this.isStatusActive()),
  );

  existingRule = output<CardTopup | null>();
  selectedCard = input<Card | null>();
  selectedRule: WritableSignal<CardTopup | null> = signal(null);
  selectedFrequency: WritableSignal<TopUpFrequency> = signal<TopUpFrequency>(TopUpFrequency.Monthly);

  constructor() {
    super();
    this.route.params.subscribe({
      next: (params: Params) => {
        this.topupRuleId.set(params['id']);
      },
    });
  }

  override createForm(): void {
    const options$ = this.cardTopupsApiService.getOptions(this.cardTopupsApiService.URL_TOPUP_RULE + '/', Method.POST);
    const data$ = this.getInitialData();
    this.subscriptions.add(
      this.buildFromOptions(options$, data$).subscribe({
        next: () => {
          this.handleTopUpAmountChanges();
          if (data$) {
            data$?.subscribe({
              next: (rule: CardTopupRequest) => {
                if (this.isRecurring()) {
                  this.form.get('date_end')?.disable();
                  this.form.get('date_start')?.disable();
                  this.selectedFrequency.set(rule.frequency ?? TopUpFrequency.Monthly);
                } else {
                  this.dateFrom.set(this.ngbDateParserFormatter.parse(rule.date_start));
                  this.dateTo.set(this.ngbDateParserFormatter.parse(rule.date_end));
                  if (!this.isStatusPending() && this.topupRuleId()) this.form.get('amount')?.disable();
                }
              },
            });
          }
        },
        error: (error: HttpErrorResponse) => {
          this.fetchError.set({
            status: error.status,
          });
        },
      }),
    );
  }

  getInitialData() {
    if (!this.topupRuleId()) return of({} as CardTopupRequest);
    return this.cardTopupsApiService.getTopupRuleById(this.topupRuleId()).pipe(
      map((rule) => {
        this.existingRule.emit(rule);
        if (rule?.schedule_status !== CardScheduleStatus.Pending && rule.date_start) {
          this.staticDateDisplay.set(formatDateDash(rule.date_start) + ' - ' + formatDateDash(rule.date_end));
        }
        this.selectedRule.set(rule);
        return rule as unknown as CardTopupRequest;
      }),
    );
  }

  protected handleTopUpAmountChanges(): void {
    this.form.get('amount')?.valueChanges.subscribe((amount) => {
      if (amount === 0) this.form.get('amount')?.setValue(undefined, { emitEvent: false });
    });
  }

  handleCardLimitError($event: boolean) {
    this.hasCardLimitError.set($event);
  }

  handleFrequencyChange(value: TopUpFrequency) {
    this.selectedFrequency.set(value);
  }

  setToDate(dateString: string | null) {
    this.form.get('date_end')?.setValue(formatDateSlash(dateString));
  }

  setFromDate(dateString: string | null) {
    this.form.get('date_start')?.setValue(formatDateSlash(dateString));
  }

  override onSubmit() {
    if (this.isRecurring() && !this.topupRuleId() && this.selectedCard()?.recurring_rule_id) {
      const modalRef = this.modalService.open(ConfirmationRemoveModalComponent, {
        centered: true,
        size: 'md',
      });
      modalRef.componentInstance.action = `replace`;
      modalRef.componentInstance.subject = `this rule`;
      modalRef.componentInstance.removeMessage = `If you continue, this card's rule will be replaced with the provided data.`;
      modalRef.componentInstance.confirmationBtnText = `Replace rule`;
      modalRef.closed.subscribe({
        next: () => {
          super.onSubmit();
        },
      });
    } else {
      super.onSubmit();
    }
  }

  override submitData(formData: CardTopupRequest): Observable<CardTopupRequest> {
    formData.card = <number>this.selectedCard()?.id ?? this.selectedRule()?.card?.id;
    if (this.isRecurring()) {
      formData.type = 1;
      formData.frequency = this.selectedFrequency() ?? 3;
    } else {
      formData.type = 2;
    }
    return this.cardTopupsApiService.createTopupRule(formData);
  }

  handleSubmitSuccess(data: CardTopupRequest): void {
    this.offcanvasService.close();
    // if existing rule
    if (this.topupRuleId() && this.isStatusPending()) {
      // if its Pending AND dates have been changed, delete the "old" one because a new one will get created
      if (this.datesHaveChanged(this.selectedRule(), data)) {
        this.cardTopupsApiService.removeTopupRule(this.topupRuleId()).subscribe({
          next: () => {
            this.showToastUpdateSuccessful();
          },
        });
      } else {
        // only amount has changed, no new rules created so just show the toast.
        this.showToastUpdateSuccessful();
      }
    } else if (!this.topupRuleId()) {
      // if new rule
      this.toastService.show(
        `You have successfully created a ${this.isRecurring() ? 'recurring' : 'scheduled'} top-up rule`,
        'iconoir-check-circle',
      );
    }
    this.offcanvasService.offCanvasNavigate(`/card-topups/${this.isRecurring() ? 'recurring' : 'scheduled'}`);
  }

  showToastUpdateSuccessful() {
    this.toastService.show(
      `You have successfully updated a ${this.isRecurring() ? 'Recurring' : 'Scheduled'} top-up rule.`,
      'iconoir-check-circle',
    );
  }

  datesHaveChanged(oldCard: CardTopup | null, newCard: CardTopupRequest) {
    const startDateSame = oldCard?.date_start === newCard.date_start;
    const endDateSame = oldCard?.date_end === newCard.date_end;
    return !startDateSame || !endDateSame;
  }

  goToImmediateTopup() {
    this.offcanvasService.offCanvasNavigate(`/card-topups/immediate/add`);
  }

  onRemoveTopupRule() {
    const modalRef = this.modalService.open(ConfirmationRemoveModalComponent, {
      centered: true,
      size: 'md',
    });
    modalRef.componentInstance.subject = `this rule`;
    if (this.isStatusActive()) {
      modalRef.componentInstance.action = `stop`;
      modalRef.componentInstance.confirmationBtnText = 'Stop rule';
      modalRef.componentInstance.removeMessage = `By stopping this rule, the money will be removed from this card immediately, unless another rule is active for this card.`;
    } else if (this.isStatusPending() || this.isRecurring()) {
      modalRef.componentInstance.removeMessage = `Any balance will remain on the card. The money can be removed in the immediate card top-up section.`;
    }

    modalRef.closed.subscribe({
      next: () => {
        this.cardTopupsApiService.removeTopupRule(this.topupRuleId()).subscribe({
          next: () => {},
          complete: () => {
            this.offcanvasService.close(this.returnUrl());
            this.toastService.show(
              `You have successfully ${this.isStatusActive() ? 'stopped' : 'removed'} a ${this.isRecurring() ? 'recurring' : 'scheduled'} top-up rule.`,
              'iconoir-check-circle',
            );
          },
          error: () => {
            this.toastService.show('Something went wrong. This top-up rule could not be removed.');
          },
        });
      },
    });
  }
}
